//@ts-nocheck
import ManageBookingSkeleton from './ManageBookingSkeleton/ManageBookingSkeleton';
import BookingTotalsHeader from './BookingTotalsHeader/BookingTotalsHeader';
import BookingPricingPlansPanel from './BookingPricingPlansPanel/BookingPricingPlansPanel';
import ConfirmUpdateModal from './ConfirmUpdateModal/ConfirmUpdateModal';
import ConfirmDeleteModal from './ConfirmDeleteModal/ConfirmDeleteModal';
import { ErrorToast, SuccessToast } from '../../../../utils/alerts';
import { Box } from '@mui/material';
import { XGButton } from '@xg-cl/xg-cl';
import { useState, useEffect, useCallback } from 'react';
import { CaretLeft } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { getTripPricingPlan } from '../../../../services/trip/trip';
import {
  getManageBookingById,
  deleteManageBookingPassengers,
  updateBookingPlan
} from '../../../../services/booking/booking';
import * as PropTypes from 'prop-types';
import './ManageBooking.css';

/**
 * Component that renders the Manage Booking Panel.
 *
 * @param {number|string} tripId - the trip id of the booking.
 * @param {number|string} bookingId - the booking id to manage.
 * @param {Function} [handleManageBookingClose] - a function to close the panel.
 * @param {Function} [refreshBookings] - a function to refresh the bookings.
 * @param {*} [rest] - additional props to pass to the component.
 *
 * @returns {ReactElement} the Manage Booking Panel component.
 */
const ManageBooking = ({
  tripId,
  bookingId,
  handleManageBookingClose = () => {},
  refreshBookings = () => {},
  ...rest
}) => {
  const { t } = useTranslation(['bookings']);
  const [bookingData, setBookingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPassengerRemoving, setIsPassengerRemoving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tripPricingPlans, setTripPricingPlans] = useState([]);
  const [updatedBookingPlan, setUpdatedBookingPlan] = useState(null);

  const checkedPassengers = bookingData?.bookingplans
    ?.flatMap((bookingplan) => bookingplan?.passengers || [])
    ?.filter((passenger) => passenger?.checked);

  const hasAnyPassengers = bookingData?.bookingplans?.some(
    (bookingplan) =>
      Array.isArray(bookingplan?.passengers) &&
      bookingplan.passengers.length > 0
  );

  const fetchBookingData = useCallback(async () => {
    try {
      const { data } = await getManageBookingById(bookingId);

      setBookingData(data);
    } catch (error) {
      setBookingData(null);
    }
  }, [bookingId]);

  const fetchTripPricingPlansData = useCallback(async () => {
    try {
      const { data } = await getTripPricingPlan(tripId);

      setTripPricingPlans(data?.pricingPlans || []);
    } catch (error) {
      setTripPricingPlans([]);
    }
  }, [tripId]);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    await Promise.all([fetchBookingData(), fetchTripPricingPlansData()]);

    setIsLoading(false);
  }, [fetchBookingData, fetchTripPricingPlansData]);

  const unCkeckAllPassengers = () => {
    const updatedBookingplans = bookingData?.bookingplans?.map(
      (bookingplan) => {
        const updatedPassengers = bookingplan?.passengers?.map((passenger) => {
          return { ...passenger, checked: false };
        });
        return { ...bookingplan, passengers: updatedPassengers };
      }
    );

    setBookingData({ ...bookingData, bookingplans: updatedBookingplans });
  };

  const onCancelPassengerRemove = () => {
    unCkeckAllPassengers();
    setIsPassengerRemoving(false);
  };

  const onConfirmPassengerRemove = () => {
    setIsDeleteModalOpen(true);
  };

  const onSubmitRemovePassenger = async () => {
    try {
      setIsSubmitting(true);

      const idsToRemove = checkedPassengers?.map((passenger) => {
        return passenger?.passengerid;
      });

      const { data } = await deleteManageBookingPassengers(bookingId, {
        passengerids: idsToRemove
      });

      if (data && data?.deletedCount > 0) {
        await Promise.all([fetchBookingData(), refreshBookings()]);

        setIsPassengerRemoving(false);
        setIsDeleteModalOpen(false);
        SuccessToast(t('bookings:passengerRemovedSuccess'));
      } else {
        ErrorToast(t('bookings:passengerRemovedError'));
      }
    } catch (error) {
      ErrorToast(t('bookings:passengerRemovedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCloseRemovalModal = () => {
    setIsDeleteModalOpen(false);
    setIsSubmitting(false);
  };

  const onCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setUpdatedBookingPlan(null);
  };

  const onConfirmUpdateModal = async () => {
    try {
      setIsSubmitting(true);

      const { bookingPlanId, newTripPlan } = updatedBookingPlan;

      const { data } = await updateBookingPlan(bookingId, bookingPlanId, {
        newTripPlanId: newTripPlan?.tripplanid
      });

      if (data) {
        await Promise.all([fetchBookingData(), refreshBookings()]);

        SuccessToast(t('bookings:pricingPlanUpdatedSuccess'));
        setUpdatedBookingPlan(null);
        setIsUpdateModalOpen(false);
      } else {
        ErrorToast(t('bookings:pricingPlanUpdatedError'));
      }
    } catch (error) {
      ErrorToast(t('bookings:pricingPlanUpdatedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onChangePricingPlan = (bookingPlanId, oldTripPlanId, newTripPlanId) => {
    if (
      bookingPlanId &&
      oldTripPlanId &&
      newTripPlanId &&
      oldTripPlanId != newTripPlanId &&
      checkedPassengers?.length === 0
    ) {
      const newTripPlan = tripPricingPlans?.find(
        (tripPricingPlan) => tripPricingPlan?.tripplanid == newTripPlanId
      );

      const oldTripPlan = bookingData?.bookingplans?.find(
        (bookingplan) => bookingplan?.tripplanid == oldTripPlanId
      );

      const planPassegers =
        bookingData?.bookingplans?.find(
          (bookingplan) => bookingplan?.bookingplanid == bookingPlanId
        )?.passengers || [];

      setUpdatedBookingPlan({
        bookingPlanId,
        bookingId,
        newTripPlan,
        oldTripPlan: oldTripPlan?.tripplan,
        passengers: planPassegers
      });

      unCkeckAllPassengers();
      setIsUpdateModalOpen(true);
    }
  };

  useEffect(() => {
    if (bookingId) {
      loadData();
    }
  }, [bookingId, loadData]);

  if (isLoading) {
    return <ManageBookingSkeleton id={'manage-booking-skeleton'} />;
  }

  return (
    <Box
      className='booking-panel-container'
      id={'manage-booking-panel'}
      {...rest}
    >
      <Box className='booking-panel__main-header'>
        <Box className='booking-breadcrumb__container'>
          <Box
            className='booking-breadcrumb__item'
            onClick={handleManageBookingClose}
          >
            <CaretLeft size={24} />
            <span>{t('bookings:bookings')}</span>
          </Box>
        </Box>
        <Box className='booking-panel__main-header__manage-booking'>
          <h2 className='booking-panel__main-header__manage-booking__title'>
            {t('bookings:manageBooking')} #{bookingId || ''}
          </h2>
          {!isPassengerRemoving && (
            <XGButton
              className='booking-panel__remove-passenger-button'
              data-testid='remove-passenger-button'
              id='remove-passenger-button'
              text={t('bookings:removePassenger')}
              type='error'
              size='large'
              variant='outlined'
              onClick={() => setIsPassengerRemoving((prev) => !prev)}
              disabled={!hasAnyPassengers}
            />
          )}
        </Box>
      </Box>
      <BookingTotalsHeader id={'booking-totals-header'} data={bookingData} />
      <BookingPricingPlansPanel
        id={'booking-pricing-plans-panel'}
        data={bookingData}
        setBookingData={setBookingData}
        isPassengerRemoving={isPassengerRemoving}
        checkedPassengers={checkedPassengers}
        tripPricingPlans={tripPricingPlans}
        onChangePricingPlan={onChangePricingPlan}
      />

      {isPassengerRemoving && (
        <Box className='booking-panel__footer-cta__container'>
          <XGButton
            data-testid='cancel-remove-passenger-button'
            id='cancel-remove-passenger-button'
            text={t('common:cancel')}
            type='secondary'
            size='large'
            variant='outlined'
            onClick={onCancelPassengerRemove}
          />
          <XGButton
            data-testid='confirm-remove-passenger-button'
            id='confirm-remove-passenger-button'
            text={t('common:remove')}
            type='primary'
            size='large'
            variant='contained'
            onClick={onConfirmPassengerRemove}
            disabled={!checkedPassengers?.length}
          />
        </Box>
      )}

      <ConfirmDeleteModal
        id={'confirm-delete-modal'}
        isOpen={isDeleteModalOpen}
        isSubmitting={isSubmitting}
        checkedPassengers={checkedPassengers}
        bookingData={bookingData}
        onClose={onCloseRemovalModal}
        onCancel={() => setIsDeleteModalOpen(false)}
        onSubmit={onSubmitRemovePassenger}
      />

      <ConfirmUpdateModal
        id={'confirm-update-modal'}
        isOpen={isUpdateModalOpen}
        updatedBookingPlan={updatedBookingPlan}
        bookingData={bookingData}
        onClose={onCloseUpdateModal}
        onSubmit={onConfirmUpdateModal}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
};

ManageBooking.propTypes = {
  tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleManageBookingClose: PropTypes.func,
  refreshBookings: PropTypes.func
};

export default ManageBooking;
